import React, { useState, useMemo } from 'react';

const Pricing = () => {
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);

  // Memoize the static data to optimize performance
  const pricingData = useMemo(() => [
    {
      category: 'Website Type',
      items: [
        { name: 'Basic Business Website (Up to 5 Pages)', price: '$1,000 - $2,500' },
        { name: 'E-commerce Website (Up to 50 Products)', price: '$2,500 - $5,000' },
        { name: 'Custom Web Application', price: 'Starting at $5,000' },
      ],
    },
    {
      category: 'Features & Integrations',
      items: [
        { name: 'Custom Design', price: '$300 - $1,200' },
        { name: 'CMS Integration (e.g., WordPress)', price: '$500 - $1,500' },
        { name: 'E-commerce Functionality', price: '$800 - $2,500' },
        { name: 'SEO Optimization', price: '$300 - $1,000' },
        { name: 'Blog Setup', price: '$200 - $800' },
      ],
    },
    {
      category: 'Maintenance & Support',
      items: [
        { name: 'Monthly Maintenance', price: '$100 - $300/mo' },
        { name: 'Security & Updates', price: '$200 - $800/year' },
        { name: 'Performance Optimization', price: '$300 - $1,000' },
      ],
    },
    {
      category: 'Process Automation with Microsoft Power Automate',
      items: [
        { name: 'Basic Automation (Up to 5 Flows)', price: '$500 - $1,500' },
        { name: 'Advanced Automation (Up to 20 Flows)', price: '$1,500 - $3,500' },
        { name: 'Custom Automation Solutions', price: 'Starting at $3,500' },
      ],
    },
    {
      category: 'Additional Services',
      items: [
        { name: 'Content Creation', price: '$100 - $500/page' },
        { name: 'Logo & Branding Design', price: '$300 - $1,500' },
        { name: 'Consulting & Strategy', price: '$100 - $250/hour' },
      ],
    },
  ], []);

  const faqData = useMemo(() => [
    { question: 'How long does it take to build a website?', answer: 'The timeline depends on the complexity and features required. A basic website can take 2-4 weeks, while custom applications may take several months.' },
    { question: 'Do you provide ongoing maintenance?', answer: 'Yes, we offer monthly and annual maintenance plans to keep your website secure, updated, and running smoothly.' },
    { question: 'Are there any hidden fees?', answer: 'No, we believe in transparent pricing. All costs will be clearly outlined in your custom quote.' },
    { question: 'Can I update my website content myself?', answer: 'Absolutely! We offer CMS integration, such as WordPress, allowing you to easily manage and update your content.' },
    { question: 'Do you offer custom solutions?', answer: 'Yes, we provide custom web development tailored to your unique business needs and goals.' },
  ], []);

  return (
    <section className="min-h-screen flex flex-col md:flex-row">
      {/* Left Section: Pricing Table */}
      <div className="w-full md:w-1/2 bg-white pt-28 p-8">
        <h2 className="text-4xl font-bold mb-6">Web Development Pricing
          <img src="/images/unterstrch.png" alt="Underline" className="relative left-1/4 transform -translate-x-3/4 bottom-4 w-1/3 opacity-80 z-20" />
        </h2>

        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="py-4 px-6 text-gray text-left text-lg uppercase font-semibold relative">
                Service
                <img src="/images/unterstrch.png" alt="Underline" className="absolute left-1/12 bottom-2 w-1/3 opacity-80" />
              </th>
              <th className="py-4 px-6 text-gray text-left text-lg uppercase font-semibold relative">
                Price Range
                <img src="/images/unterstrch.png" alt="Underline" className="absolute left-778 bottom-1.5 w-3/4 opacity-80" />
              </th>
            </tr>
          </thead>
          <tbody>
            {pricingData.map((category, index) => (
              <React.Fragment key={index}>
                <tr>
                  <td className="py-4 px-6 text-l font-bold" colSpan={2}>{category.category}</td>
                </tr>
                {category.items.map((item, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-4 px-6 text-gray-800">{item.name}</td>
                    <td className="py-4 px-6 text-gray-600">{item.price}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Right Section: Background Image */}
      <div className="hidden md:block w-1/2 bg-cover relative" style={{ backgroundImage: "url('/images/background_pricing.jpg')" }}>
        <div className="absolute inset-0 flex flex-col justify-center items-center space-y-6">
          <div className="w-3/4 bg-white p-8 rounded-lg shadow-lg bg-opacity-90">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 relative inline-block">
              FAQ
              <img src="/images/unterstrch.png" alt="Underline" className="absolute left-3/4 transform -translate-x-3/4 -bottom-1 w-1/8 h-4 opacity-80 z-20" />
            </h2>
            {faqData.map((faq, index) => (
              <div key={index} className="mb-6">
                <button
                  className="w-full text-left text-xl font-semibold text-gray-800 focus:outline-none flex justify-between items-center"
                  onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                >
                  {faq.question}
                  <span>{activeFAQ === index ? '-' : '+'}</span>
                </button>
                <p className={`mt-2 text-gray-600 transition-all duration-300 ${activeFAQ === index ? 'max-h-40' : 'max-h-0 overflow-hidden'}`}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>

          {/* Request a Quote Card */}
          <div className="w-3/4 bg-white rounded-lg shadow-lg bg-opacity-90 py-10 flex justify-center">
            <button type="submit" className="text-slate-600 hover:text-slate-700 transition text-xl font-bold rounded-lg text-center">
              Request your Quote today!
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
